import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, Avatar, fmtDateTime, timeAgo, StatusBadge, inr } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Search, Send, Phone, ListChecks, MessageSquare, MessageCircle, StickyNote,
  Sparkles, ArrowLeft, Paperclip, Pencil, Save, X,
} from "lucide-react";
import { toast } from "sonner";

export default function WhatsApp() {
  const { user } = useAuth();
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null); // student_id
  const [detail, setDetail] = useState(null);
  const [msg, setMsg] = useState("");
  const [search, setSearch] = useState("");
  const endRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});

  useEffect(() => { api.get("/whatsapp/conversations").then(({ data }) => { setConvs(data); if (data[0]) openChat(data[0].student_id); }); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [detail?.whatsapp_messages?.length]);

  const openChat = async (sid) => {
    setActive(sid); setDetail(null); setEditing(false);
    const { data } = await api.get(`/students/${sid}`);
    setDetail(data);
  };
  const startEdit = () => { setEdit({ name: detail.name, mobile: detail.mobile, alt_mobile: detail.alt_mobile, parent_name: detail.parent_name, gender: detail.gender, city: detail.city, state: detail.state, email: detail.email }); setEditing(true); };
  const saveEdit = async () => {
    try { await api.patch(`/students/${active}`, edit); toast.success("Lead details updated"); setEditing(false); openChat(active); }
    catch (e) { toast.error("Update failed"); }
  };

  const send = async () => {
    if (!msg.trim() || !active) return;
    await api.post(`/whatsapp/${active}`, { body: msg });
    setMsg("");
    openChat(active);
    api.get("/whatsapp/conversations").then(({ data }) => setConvs(data));
  };

  const quickCall = async () => {
    await api.post("/calls", { student_id: active, disposition: "Connected", duration: 45, notes: "Call from WhatsApp inbox" });
    toast.success("Call logged (telephony MOCKED)");
    openChat(active);
  };

  if (convs === null) return <Loading />;
  const filtered = convs.filter((c) => !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.mobile.includes(search));
  const lead = detail?.leads?.[0];

  return (
    <div className="h-[calc(100vh-8rem)] -m-4 sm:-m-6 lg:-m-8">
      <div className="h-full grid grid-cols-1 lg:grid-cols-[300px_1fr_330px] bg-white">
        {/* Pane 1: conversation list */}
        <aside className={`border-r border-slate-200 flex flex-col ${active ? "hidden lg:flex" : "flex"}`}>
          <div className="h-14 flex items-center gap-2 px-4 border-b border-slate-200 bg-emerald-600 text-white">
            <MessageCircle className="w-5 h-5" />
            <span className="font-heading font-bold text-sm">OhCampus — Admission Portal</span>
          </div>
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search lead(s)" className="pl-9 h-9 bg-slate-50" data-testid="wa-search" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.map((c) => (
              <button key={c.student_id} onClick={() => openChat(c.student_id)} data-testid="wa-conv-item"
                className={`w-full flex items-center gap-3 px-3 py-3 border-b border-slate-50 text-left hover:bg-slate-50 ${active === c.student_id ? "bg-emerald-50" : ""}`}>
                <Avatar name={c.name} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between"><span className="font-semibold text-slate-800 text-sm truncate">{c.name}</span><span className="text-[10px] text-slate-400">{timeAgo(c.at)}</span></div>
                  <div className="text-xs text-slate-500 truncate">{c.last}</div>
                </div>
                {c.count > 0 && <span className="text-[10px] bg-emerald-500 text-white rounded-full w-5 h-5 flex items-center justify-center font-bold">{c.count}</span>}
              </button>
            ))}
          </div>
        </aside>

        {/* Pane 2: chat */}
        <section className={`flex flex-col min-w-0 ${active ? "flex" : "hidden lg:flex"}`} style={{ backgroundColor: "#ECE5DD" }}>
          {!detail ? <div className="flex-1 flex items-center justify-center text-slate-400">Select a conversation</div> : (
            <>
              <div className="h-14 bg-white flex items-center gap-3 px-4 border-b border-slate-200">
                <button className="lg:hidden" onClick={() => setActive(null)}><ArrowLeft className="w-5 h-5 text-slate-500" /></button>
                <Avatar name={detail.name} size={36} />
                <div className="flex-1 min-w-0"><div className="font-heading font-bold text-slate-800">{detail.name}</div><div className="text-[11px] text-slate-400">{detail.mobile}</div></div>
                {lead && <StatusBadge value={lead.stage} />}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {(detail.whatsapp_messages || []).map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[70%] px-3 py-2 rounded-lg text-sm shadow-sm ${m.direction === "outbound" ? "bg-[#DCF8C6] text-slate-800" : "bg-white text-slate-800"}`}>
                      {m.body}
                      <div className="text-[10px] text-slate-400 mt-1 text-right">{fmtDateTime(m.created_at)}</div>
                    </div>
                  </div>
                ))}
                {(!detail.whatsapp_messages || detail.whatsapp_messages.length === 0) && <div className="text-center text-sm text-slate-400 py-8">No messages yet</div>}
                <div ref={endRef} />
              </div>
              <div className="bg-white border-t border-slate-200 p-3 flex items-center gap-2 sticky bottom-0 z-10 shrink-0">
                <Paperclip className="w-5 h-5 text-slate-400" />
                <Input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Type a message… (send MOCKED)" className="flex-1 bg-slate-50" data-testid="wa-input" />
                <Button onClick={send} className="bg-emerald-600 hover:bg-emerald-700 h-10 w-10 p-0" data-testid="wa-send"><Send className="w-4 h-4" /></Button>
              </div>
            </>
          )}
        </section>

        {/* Pane 3: lead panel */}
        <aside className="hidden lg:flex flex-col border-l border-slate-200 overflow-y-auto">
          {detail && (
            <>
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-3"><Avatar name={detail.name} size={40} /><div><div className="font-heading font-bold text-slate-800">{detail.name}</div>{lead && <div className="text-[11px] text-[#4F46E5] font-semibold">Score {lead.score} · {lead.lead_category}</div>}</div></div>
                {!editing ? <Button size="sm" variant="outline" className="h-8" onClick={startEdit} data-testid="wa-edit-lead"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                  : <div className="flex gap-1"><Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={saveEdit} data-testid="wa-save-lead"><Save className="w-3.5 h-3.5" /></Button><Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEditing(false)}><X className="w-3.5 h-3.5" /></Button></div>}
              </div>
              {!editing ? (
                <div className="p-4 grid grid-cols-2 gap-y-3 gap-x-2 text-xs border-b border-slate-100">
                  <Field k="Father" v={detail.parent_name} /><Field k="Alt Phone" v={detail.alt_mobile} />
                  <Field k="Gender" v={detail.gender} /><Field k="City" v={detail.city} />
                  <Field k="Phone" v={detail.mobile} /><Field k="State" v={detail.state} />
                  <Field k="Course" v={lead?.course} /><Field k="Budget" v={lead ? inr(lead.budget) : ""} />
                </div>
              ) : (
                <div className="p-4 grid grid-cols-2 gap-2 text-xs border-b border-slate-100" data-testid="wa-edit-form">
                  {[["name", "Name"], ["mobile", "Phone"], ["alt_mobile", "Alt Phone"], ["email", "Email"], ["parent_name", "Father"], ["gender", "Gender"], ["city", "City"], ["state", "State"]].map(([k, l]) => (
                    <div key={k} className={k === "name" || k === "email" ? "col-span-2" : ""}>
                      <label className="text-[10px] uppercase tracking-wider text-slate-400">{l}</label>
                      <Input value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} className="h-8 mt-0.5" data-testid={`wa-edit-${k}`} />
                    </div>
                  ))}
                </div>
              )}
              {/* action bar */}
              <div className="grid grid-cols-5 gap-1 p-3 border-b border-slate-100 text-center">
                {[[Phone, "Call", quickCall], [ListChecks, "Task", () => toast.info("Task (MOCKED)")], [MessageSquare, "WA", () => document.querySelector('[data-testid="wa-input"]')?.focus()], [MessageCircle, "SMS", () => toast.info("SMS (MOCKED)")], [StickyNote, "Note", () => toast.info("Note (MOCKED)")]].map(([Icon, l, fn], i) => (
                  <button key={i} onClick={fn} data-testid={`wa-action-${l.toLowerCase()}`} className="flex flex-col items-center gap-1 py-2 rounded-lg hover:bg-slate-50 text-slate-600">
                    <Icon className="w-4 h-4 text-[#312E81]" /><span className="text-[10px]">{l}</span>
                  </button>
                ))}
              </div>
              <div className="p-4">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3 text-[#4F46E5]" /> Activity History</div>
                <div className="space-y-2 max-h-72 overflow-y-scroll pr-1" data-testid="wa-activity">
                  {(detail.activities || []).map((a) => (
                    <div key={a.id} className="text-xs border-l-2 border-emerald-200 pl-2">
                      <div className="text-slate-700">{a.action}</div>
                      <div className="text-[10px] text-slate-400">{a.actor_name} · {timeAgo(a.created_at)}</div>
                    </div>
                  ))}
                  {(!detail.activities || detail.activities.length === 0) && <div className="text-xs text-slate-400">No activity</div>}
                </div>
              </div>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

function Field({ k, v }) {
  return <div><div className="text-[10px] uppercase tracking-wider text-slate-400">{k}</div><div className="text-slate-700 font-medium truncate">{v || "—"}</div></div>;
}
